import * as React from 'react';

import {
  Button,
  cn,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
  Menu,
} from '@rematter/ui-web';
import { useSetAtom } from 'jotai';
import {
  LucideMoreHorizontal,
  LucideShare2,
  LucideTrash2,
  LucideTriangleAlert,
} from 'lucide-react';

import {
  $archiveChatById,
  $selectedChatId,
} from '../../_state/chatConversation.state';
import { useTranslations } from '../../i18n/useTranslations';
import type { ChatRecord } from '../../types';

import { ShareDialog } from './ShareDialog';

interface ChatListItemProps {
  chat: ChatRecord;
  isSelected: boolean;
  onSelect?: () => void;
}

//
// ⚛️ ⚛️ ⚛️ CHAT LIST ITEM ⚛️ ⚛️ ⚛️
//
export const ChatListItem = ({
  chat,
  isSelected,
  onSelect,
}: ChatListItemProps) => {
  const t = useTranslations();

  // States
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = React.useState(false);

  // Atoms
  const setSelectedChatId = useSetAtom($selectedChatId);
  const archiveChatById = useSetAtom($archiveChatById);

  // Memoized values
  const menuItems = React.useMemo(
    () => [
      {
        label: t.header.share,
        icon: <LucideShare2 />,
        onAction: () => setIsShareDialogOpen(true),
      },
      {
        label: t.header.delete,
        icon: <LucideTrash2 />,
        className: 'text-failure',
        onAction: () => setIsDeleteDialogOpen(true),
      },
    ],
    [t],
  );

  // Callbacks
  const handleConfirmDelete = React.useCallback(async () => {
    setIsDeleting(true);
    try {
      await archiveChatById({ chatId: chat.id });
      setIsDeleteDialogOpen(false);
    } finally {
      setIsDeleting(false);
    }
  }, [archiveChatById, chat.id]);

  const handleSelect = React.useCallback(() => {
    setSelectedChatId(chat.id);
    onSelect?.();
  }, [setSelectedChatId, chat.id, onSelect]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setSelectedChatId(chat.id);
        onSelect?.();
      }
    },
    [setSelectedChatId, chat.id, onSelect],
  );

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        className={cn(
          '@ChatListItem',
          'group flex w-full cursor-pointer items-center gap-2 rounded-md px-4 py-2.5 text-left text-white transition-colors',
          isSelected && 'bg-white/10',
          !isSelected && 'hover:bg-white/5',
        )}
        onClick={handleSelect}
        onKeyDown={handleKeyDown}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 truncate text-sm">
            <span className="truncate">{chat.title || t.sidebar.newChat}</span>
            {chat.isShared && (
              <LucideShare2 className="size-3.5 shrink-0 text-white/40" />
            )}
          </div>
        </div>
        <div
          className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
          onClick={(e) => e.stopPropagation()}
        >
          <Menu
            trigger={
              <Button
                size="small"
                variant="ghost"
                className="size-6 min-h-0 text-white/50 hover:text-white"
                icon={<LucideMoreHorizontal className="size-4" />}
              />
            }
            items={menuItems}
          />
        </div>
      </div>

      <DeleteConfirmDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />

      <ShareDialog
        chatId={chat.id}
        isOpen={isShareDialogOpen}
        onOpenChange={setIsShareDialogOpen}
      />
    </>
  );
};

interface DeleteConfirmDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onConfirm: () => void;
  isDeleting: boolean;
}

//
// ⚛️ ⚛️ ⚛️ DELETE CONFIRM DIALOG ⚛️ ⚛️ ⚛️
//
const DeleteConfirmDialog = ({
  isOpen,
  onOpenChange,
  onConfirm,
  isDeleting,
}: DeleteConfirmDialogProps) => {
  const t = useTranslations();

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      className="@DeleteConfirmDialog"
    >
      <DialogContent position="center">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-semibold text-failure">
            <LucideTriangleAlert className="size-5" />
            {t.header.deleteDialog.title}
          </DialogTitle>
        </DialogHeader>
        <div className="p-4">
          <p className="text-sm text-neutral/70">
            {t.header.deleteDialog.message}
          </p>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onPress={() => onOpenChange(false)}
            isDisabled={isDeleting}
          >
            {t.header.deleteDialog.cancel}
          </Button>
          <Button
            variant="solid"
            onPress={onConfirm}
            isDisabled={isDeleting}
            isLoading={isDeleting}
            className="bg-failure text-white hover:bg-failure/90"
          >
            {t.header.delete}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
